import { useCallback, useContext, useMemo, useState } from 'react';
import EAButton from 'components/common/EAButton';
import EAIcon from 'components/common/EAIcon';
import i18next from 'i18next';
import useStyles from 'style/js-style/components/thirdparties/ecovadis/EcovadisStyle';
import EADialog from 'components/common/EADialog';
import { Tab, Tabs } from '@mui/material';
import {
  EcovadisEvaluationStep,
  EvAllScores,
  getEcovadisPerformanceGraphColors,
  getEvDataScopeChangeFieldValue,
  getScorePerformanceByValue,
} from 'services/common/EcovadisUtils';
import EAEvolutionLineChart from 'components/common/charts/EAEvolutionLineChart';
import {
  PrivilegeEnum,
  RoleBasedAccessControlContext,
} from 'services/common/RolesUtils';
import { Context } from 'states/Store';
import EcovadisEvaluationDialog from '../EcovadisDialogs/EcovadisEvaluationDialog/EcovadisEvaluationDialog';
import { isEmpty } from 'lodash';
import EATooltip from 'components/common/EATooltip';
import { getEcovadisThirdpartyDocument } from 'services/edge/EcovadisService';
import { getUrlWithAccountId } from 'config/Routes';
import { ERROR, WARNING } from 'services/common/Constants';
import EcovadisStopEvaluationButton from './EcovadisStopEvaluationButton';

const HistoryBtnTooltipTitle = () => {
  const { classes } = useStyles();
  return (
    <div className={classes.historyBtnTooltipTitle}>
      <EAIcon icon="warning-snack" />
      {i18next.t('ecovadis.historyBtnTooltipTitle')}
    </div>
  );
};

const EcovadisScoresButtons = ({
  evData,
  evScoreHistory,
  onRiskChange = () => { },
}) => {
  const { classes } = useStyles();
  const [isHistoricDialogOpen, setIsHistoricDialogOpen] = useState(false);
  const [selectScoreEvolution, setSelectScoreEvolution] = useState(
    EvAllScores[0]
  );
  const [isEvaluationPopupOpen, setIsEvaluationPopupOpen] = useState(false);
  const [state, dispatch] = useContext(Context);
  const { hasAnyOfPrivileges } = useContext(RoleBasedAccessControlContext);

  const newEvaluation = () => {
    setIsEvaluationPopupOpen(true);
  };

  const goToReport = async () => {
    try {
      const accountId = state.account.id;
      const thirdpartyId = state.thirdparty.thirdpartyId;
      const result = await getEcovadisThirdpartyDocument(
        state.account.id,
        state.thirdparty.thirdpartyId
      );
      const document = result.content;
      const evidenceId = document?.evidences?.[0]?.responseId;
      const dossierId = document?.dossierId;
      if (evidenceId && dossierId) {
        const docViewerPath = `thirdparties/${thirdpartyId}/documents/${evidenceId}?dossierId=${dossierId}`;
        window.open(getUrlWithAccountId(accountId, docViewerPath), '_blank');
      } else {
        dispatch({
          type: 'ALERT',
          alert: {
            type: WARNING,
            msg: i18next.t("ecovadis.messages.reportInProgress"),
          },
        });
      }
    } catch (error) {
      dispatch({
        type: 'ALERT',
        alert: {
          type: ERROR,
          msg: error?.message?.messages?.error?.[0]?.code || 'INTERNAL_ERROR',
        },
      });
    }
  };

  const fetchAndOpenScoreEvolution = () => {
    setIsHistoricDialogOpen(true);
  };

  const historyGraphData = useMemo(() => {
    return evScoreHistory
      ?.reduce((acc, item) => {
        EvAllScores.forEach((score) => {
          const scoreValue = getEvDataScopeChangeFieldValue(item, score); // We get target scores if available after a scope change
          if (scoreValue || scoreValue === 0) {
            if (!acc[score]) acc[score] = [];
            const scorePerformance = getScorePerformanceByValue(scoreValue);
            const publishedDate = getEvDataScopeChangeFieldValue(
              item,
              'published_date'
            );
            acc[score].push({
              date: new Date(publishedDate).getTime(),
              year: new Date(publishedDate).getFullYear(),
              score: scoreValue,
              text: i18next.t(`ecovadis.performance.${scorePerformance}`),
              ...getEcovadisPerformanceGraphColors(scorePerformance),
            });
          }
        });
        return acc;
      }, {});
  }, [evScoreHistory]);

  const isEvaluationButtonDisabled = useCallback(() => {
    return (
      !state.thirdparty?.onboarded ||
      !hasAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EDIT_ECOVADIS_SOURCE)
    );
  }, [state.thirdparty, hasAnyOfPrivileges]);

  const isHistoryButtonDisabled = () => {
    return isEmpty(historyGraphData);
  };

  return (
    <>
      <EAButton
        size="small"
        onClick={goToReport}
        colorVariant="secondary"
        content={
          <>
            <span>{i18next.t('ecovadis.goToReport')}</span>
            <EAIcon icon="open-page" />
          </>
        }
      />
      <EATooltip
        title={isHistoryButtonDisabled() && <HistoryBtnTooltipTitle />}
        variant="light"
        arrow
        placement="top"
      >
        <span>
          <EAButton
            size="small"
            onClick={() => fetchAndOpenScoreEvolution()}
            disabled={isHistoryButtonDisabled()}
            colorVariant="secondary_dark"
            content={
              <>
                <EAIcon icon="history" />
                <span>{i18next.t('history')}</span>
              </>
            }
          />
        </span>
      </EATooltip>
      <EAButton
        size="small"
        onClick={newEvaluation}
        colorVariant="primary"
        customStyle={{ boxShadow: "none", minHeight: "30px" }}
        disabled={isEvaluationButtonDisabled()}
        content={
          <>
            <EAIcon icon="refresh" />
            <span>{i18next.t('ecovadis.newEvaluation')}</span>
          </>
        }
      />

      <EcovadisStopEvaluationButton
        disabled={isEvaluationButtonDisabled()}
        onRiskChange={onRiskChange}
      />

      <EADialog
        isOpen={isHistoricDialogOpen}
        title={i18next.t('ecovadis.scoreEvolution')}
        hasActions={false}
        onClose={() => setIsHistoricDialogOpen(false)}
        maxWidth="md"
      >
        <div className={classes.graphEvolutionDialog}>
          <Tabs
            value={selectScoreEvolution}
            onChange={(event, newValue) => setSelectScoreEvolution(newValue)}
            aria-label="evolution scores ecovadis tabs"
            className={classes.graphEvolutionDialogTabsWrapper}
          >
            {EvAllScores.map((score) => (
              <Tab
                key={score}
                label={i18next.t(`ecovadis.scores.${score}`)}
                value={score}
              />
            ))}
          </Tabs>
          <div className={classes.graphEvolutionContent}>
            <EAEvolutionLineChart
              id="EcovadisHistoryEvolutionChart"
              data={historyGraphData?.[selectScoreEvolution]}
            />
          </div>
        </div>
      </EADialog>

      {isEvaluationPopupOpen && (
        <EcovadisEvaluationDialog
          isOpen={isEvaluationPopupOpen}
          onClose={() => setIsEvaluationPopupOpen(false)}
          initialStep={EcovadisEvaluationStep.SUPPLIER_FORM}
          initialEVData={evData}
          onValidateSuccess={() => {
            setIsEvaluationPopupOpen(false);
            onRiskChange();
          }}
        />
      )}
    </>
  );
};

export default EcovadisScoresButtons;
